import React, { useEffect, useState, createContext, useContext, useCallback } from 'react';
import { SystemNotification } from '../types';
import { generateId } from '../utils/helpers';
import { supabase } from '../utils/supabase';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
interface SystemNotificationWithMeta extends SystemNotification {
  createdByUserId?: string;
  createdByUserName?: string;
}
interface NotificationContextType {
  notifications: SystemNotificationWithMeta[];
  unreadCount: number;
  addNotification: (notification: Omit<SystemNotification, 'id' | 'read' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => void;
}
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
export function NotificationProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [notifications, setNotifications] = useState<SystemNotificationWithMeta[]>([]);
  const isAdmin = user?.role === 'administrador';

  // Load notifications from Supabase
  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      let query = supabase.from('notifications').select('*').order('created_at', {
        ascending: false
      }).limit(100);

      // Admin sees all; others see only their own
      if (!isAdmin) {
        query = query.eq('user_id', user.id);
      }
      const {
        data,
        error
      } = await query;
      if (error) throw error;
      const mapped: SystemNotificationWithMeta[] = (data || []).map((n: any) => ({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        read: n.read,
        createdAt: new Date(n.created_at),
        relatedId: n.related_id,
        createdByUserId: n.created_by_user_id,
        createdByUserName: n.created_by_user_name
      }));
      setNotifications(mapped);
    } catch (e) {
      // Fallback to localStorage if Supabase fails
      try {
        const saved = localStorage.getItem('savidna-notifications');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            setNotifications(parsed.filter((n: any) => n?.id && n?.title).map((n: any) => ({
              ...n,
              createdAt: new Date(n.createdAt || Date.now())
            })));
          }
        }
      } catch {}
    }
  }, [user?.id, isAdmin]);
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Realtime subscription
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase.channel('notifications-realtime').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'notifications'
    }, () => {
      loadNotifications();
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, loadNotifications]);
  const addNotification = async (notification: Omit<SystemNotification, 'id' | 'read' | 'createdAt'>) => {
    const id = generateId();
    const newNotif: SystemNotificationWithMeta = {
      ...notification,
      id,
      read: false,
      createdAt: new Date(),
      createdByUserId: user?.id,
      createdByUserName: user?.name
    };

    // Show toast popup
    const toastType = notification.type === 'error' ? 'error' : notification.type === 'warning' ? 'warning' : notification.type === 'info' ? 'info' : 'success';
    showToast({
      type: toastType,
      title: notification.title,
      message: notification.message
    });

    // Optimistic update
    setNotifications(prev => [newNotif, ...prev]);
    try {
      await supabase.from('notifications').insert({
        id,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        read: false,
        related_id: notification.relatedId || null,
        user_id: user?.id || 'system',
        user_name: user?.name || 'Sistema',
        created_by_user_id: user?.id || 'system',
        created_by_user_name: user?.name || 'Sistema'
      });
    } catch (e) {
      // Fallback: keep in localStorage
      localStorage.setItem('savidna-notifications', JSON.stringify([newNotif, ...notifications].slice(0, 100)));
    }
  };
  const markAsRead = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      read: true
    } : n));
    try {
      await supabase.from('notifications').update({
        read: true
      }).eq('id', id);
    } catch {}
  };
  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      read: true
    })));
    try {
      const ids = notifications.filter(n => !n.read).map(n => n.id);
      if (ids.length > 0) {
        await supabase.from('notifications').update({
          read: true
        }).in('id', ids);
      }
    } catch {}
  };
  const clearNotification = async (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    try {
      await supabase.from('notifications').delete().eq('id', id);
    } catch {}
  };
  const clearAllNotifications = async () => {
    const ids = notifications.map(n => n.id);
    setNotifications([]);
    try {
      if (ids.length > 0) {
        await supabase.from('notifications').delete().in('id', ids);
      }
    } catch {}
  };
  const unreadCount = notifications.filter(n => !n.read).length;
  return <NotificationContext.Provider value={{
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications
  }} data-id="element-5564">
      {children}
    </NotificationContext.Provider>;
}
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
}